import { createSelector } from '@ngrx/store';

import { createDeepEqualSelector, safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import { BoundType, PaxDetailsFormData } from '@fcom/dapi';
import { asPaxTypeKey } from '@fcom/dapi/utils/pax-details-utils';
import { isNotEmpty } from '@fcom/core/utils/utils';
import { Bound } from '@fcom/dapi/api/models/bound';
import { FinnairAirBoundsResponse } from '@fcom/dapi/api/models/finnair-air-bounds-response';
import { FinnairBoundFareFamily } from '@fcom/dapi/api/models/finnair-bound-fare-family';
import { FinnairBoundItem } from '@fcom/dapi/api/models/finnair-bound-item';
import { FinnairCart } from '@fcom/dapi/api/models/finnair-cart';
import { FinnairItineraryItem } from '@fcom/dapi/api/models/finnair-itinerary-item';
import { FinnairPassengerCode } from '@fcom/dapi/api/models/finnair-passenger-code';
import { FinnairServiceItem } from '@fcom/dapi/api/models/finnair-service-item';
import { Offer } from '@fcom/dapi/api/models/offer';
import { OfferList } from '@fcom/dapi/api/models/offer-list';
import { mapServicesToBookingSummary } from '@fcom/common-booking/store/utils/services.utils';
import { BookingSummaryBound, BookingSummaryPassenger, BookingSummaryService } from '@fcom/common-booking';
import { formatDateOfBirth } from '@fcom/common-booking/utils/date-utils';
import { priceForPassengerTypeCode } from '@fcom/common-booking/utils/shopping-cart.utils';
import { cloneDeep } from '@fcom/core/utils/merge';
import { PreciseNumber } from '@fcom/core/utils/precise-number';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { BoundFareInformation, FareFamilyMap } from '@fcom/common/interfaces/booking/booking-offer-list.interface';
import { combineServices } from '@fcom/common-booking/modules/ancillaries/utils/ancillary.utils';
import {
  getProductTagServiceFlags,
  ProductTagServiceFlags,
} from '@fcom/common-booking/modules/ancillaries/utils/ancillaries.utils';
import {
  inboundsSelector,
  outboundsSelector,
  selectedInboundAirBoundIdSelector,
  selectedInboundFareFamilySelector,
  selectedInboundIdBoundsSelector,
  selectedOutboundAirBoundIdSelector,
  selectedOutboundFareFamilySelector,
  selectedOutboundIdBoundsSelector,
} from '@fcom/common/store/selectors/bounds.selector';

import { cartDataSelector } from './cart.selector';
import {
  _currentOffer as currentOfferSelector,
  currentOffersSelector,
  fareFamiliesSelector,
  selectedInboundFareFamilyCodeSelector,
  selectedInboundIdSelector,
  selectedOutboundFareFamilyCodeSelector,
  selectedOutboundIdSelector,
} from './offers.selector';
import { paxDetailTravelerSelector } from './pax-details.selector';

const _createBookingSummaryBound = (
  bound: FinnairBoundItem,
  boundFareInformation: BoundFareInformation[],
  type: BoundType
): BookingSummaryBound => {
  const generateItinerary = (): FinnairItineraryItem[] => {
    if (boundFareInformation) {
      return bound.itinerary.reduce(
        (all, it) => {
          if (it.type === 'FLIGHT') {
            all.arr.push({
              ...it,
              cabinClass: boundFareInformation?.[all.index]
                ? boundFareInformation[all.index].cabinClass.toLowerCase()
                : undefined,
              bookingClass: boundFareInformation?.[all.index]
                ? boundFareInformation[all.index].bookingClass
                : undefined,
            });
            all.index = all.index + 1;
          } else {
            all.arr.push(it);
          }
          return all;
        },
        { index: 0, arr: [] }
      ).arr;
    }
    return bound.itinerary;
  };
  return {
    ...bound,
    itinerary: generateItinerary(),
    type,
  };
};

const _selectedOfferSummaryProjector =
  (type: BoundType) =>
  (
    flightOffers: OfferList,
    currentOffer: Offer,
    boundId: string,
    fareFamilies: FareFamilyMap,
    selectedFareFamilyCode: string
  ): BookingSummaryBound => {
    if (!flightOffers || !boundId) {
      return;
    }

    const journeyDirection = type === BoundType.outbound ? 'outbounds' : 'inbounds';
    const bound: Bound = flightOffers?.[journeyDirection]?.[boundId];
    const boundFareInformation: BoundFareInformation[] = currentOffer
      ? type === BoundType.outbound
        ? currentOffer.outboundFareInformation
        : currentOffer.inboundFareInformation || []
      : [];
    const fareFamilyName = fareFamilies?.[selectedFareFamilyCode]?.brandName;

    const boundWithFareFamilyDetails = {
      ...bound,
      fareFamily: {
        ...((bound as Bound & { fareFamily?: Record<string, unknown> }).fareFamily ?? {}),
        name: fareFamilyName,
      },
    } as unknown as FinnairBoundItem;

    const summaryBound: FinnairBoundItem = fareFamilyName
      ? boundWithFareFamilyDetails
      : (bound as unknown as FinnairBoundItem);

    return _createBookingSummaryBound(summaryBound, boundFareInformation, type);
  };

const _selectedOfferOutboundSummary = createSelector(
  currentOffersSelector,
  currentOfferSelector,
  selectedOutboundIdSelector,
  fareFamiliesSelector,
  selectedOutboundFareFamilyCodeSelector,
  _selectedOfferSummaryProjector(BoundType.outbound)
);

const _selectedOfferInboundSummary = createSelector(
  currentOffersSelector,
  currentOfferSelector,
  selectedInboundIdSelector,
  fareFamiliesSelector,
  selectedInboundFareFamilyCodeSelector,
  _selectedOfferSummaryProjector(BoundType.inbound)
);

const _selectedAirBoundSummaryProjector =
  (type: BoundType) =>
  (bounds: FinnairAirBoundsResponse, boundId: string, fareFamilyId: string): BookingSummaryBound => {
    if (!bounds || !boundId || !fareFamilyId) {
      return;
    }

    const bound = bounds.boundGroups.find((boundGroup) => boundGroup.sortingId === boundId);

    if (!bound) {
      return;
    }

    const boundFareFamily = bound.fareFamilies.find(({ id }) => id === fareFamilyId);

    if (!boundFareFamily) {
      return;
    }

    const { fareFamilyCode, fareInformation } = boundFareFamily;

    const enrichedBound: FinnairBoundItem = bound
      ? {
          ...bound.details,
          fareFamily: {
            ...bound.details.fareFamily,
            name: bounds.fareFamilies[fareFamilyCode]?.brandName,
          },
        }
      : undefined;

    return _createBookingSummaryBound(enrichedBound, fareInformation, type);
  };

const _selectedAirBoundOutboundSummary = createSelector(
  outboundsSelector,
  selectedOutboundIdBoundsSelector,
  selectedOutboundAirBoundIdSelector,
  _selectedAirBoundSummaryProjector(BoundType.outbound)
);

const _selectedAirBoundInboundSummary = createSelector(
  inboundsSelector,
  selectedInboundIdBoundsSelector,
  selectedInboundAirBoundIdSelector,
  _selectedAirBoundSummaryProjector(BoundType.inbound)
);

const _cartOfferSummaries = createSelector(cartDataSelector, (cartData: FinnairCart): BookingSummaryBound[] => {
  const bounds = cartData?.bounds || [];

  return bounds.map((bound, index) =>
    cartBoundToBookingSummaryBound(bound, index === 0 ? BoundType.outbound : BoundType.inbound)
  );
});

const _airBoundSummaries = createSelector(
  _selectedAirBoundOutboundSummary,
  _selectedAirBoundInboundSummary,
  (outbound, inbound): BookingSummaryBound[] => [outbound, inbound].filter(Boolean)
);

const _offerSummaries = createSelector(
  _selectedOfferOutboundSummary,
  _selectedOfferInboundSummary,
  (outbound, inbound): BookingSummaryBound[] => [outbound, inbound].filter(Boolean)
);

export const currentOfferSelectionSelector = createSelector(
  _cartOfferSummaries,
  _airBoundSummaries,
  _offerSummaries,
  (
    cartOfferSummaries: BookingSummaryBound[],
    airBoundSummaries: BookingSummaryBound[],
    offerSummaries: BookingSummaryBound[]
  ): BookingSummaryBound[] =>
    [cartOfferSummaries, airBoundSummaries, offerSummaries].find((bookingSummaryBounds) =>
      isNotEmpty(bookingSummaryBounds)
    ) || []
);

const _currentPassengers = createSelector(paxDetailTravelerSelector, (paxDetail) => {
  return paxDetail
    .filter((pax) => pax.type !== FinnairPassengerCode.INF)
    .map((p) => paxDetailFormToBookingSummaryTraveler(p, paxDetail));
});

export const selectCurrentServices = createSelector(cartDataSelector, (data) =>
  mapServicesToBookingSummary(data?.prices.unpaid?.services, data?.passengers)
);

const _combinedServices = createSelector(cartDataSelector, (cartData) =>
  combineServices([cartData?.services?.included ?? [], cartData?.services?.unpaid ?? []])
);
export const combinedCartServicesMapSelector = createDeepEqualSelector(_combinedServices, (_services) =>
  _services.reduce((categories, service) => {
    categories[service.category] = cloneDeep(service);
    return categories;
  }, {})
);
// Only categories with a flag set, so adding or removing a plain service keeps the same output
export const combinedCartProductTagFlagsSelector = createDeepEqualSelector(
  _combinedServices,
  (services): Record<string, ProductTagServiceFlags> =>
    services.reduce((flagsByCategory, service) => {
      const flags = getProductTagServiceFlags(service);
      return flags.hasCorporateSeatBenefit || flags.hasTierBenefit
        ? { ...flagsByCategory, [service.category]: flags }
        : flagsByCategory;
    }, {})
);

export const paxDetailFormToBookingSummaryTraveler = (
  t: PaxDetailsFormData,
  allPassengers: PaxDetailsFormData[]
): BookingSummaryPassenger => {
  const infant = allPassengers.find((i) => i.travelling === t.id);
  return {
    firstName: t.firstName,
    lastName: t.lastName,
    type: `passenger.${asPaxTypeKey(t.type).toLowerCase()}`,
    withInfantFullName: infant ? `${infant.firstName || ''} ${infant.lastName || ''}` : '',
    email: t.email,
    birthDate: t.birthDate ? formatDateOfBirth(t.birthDate) : null,
    phone:
      t.phone?.phoneNumber && t.phone?.phonePrefix
        ? `+${t.phone.phonePrefix.split('|')[1]} ${t.phone.phoneNumber}`
        : null,
    frequentFlyerCard: t.frequentFlyerCard,
  };
};

export const cartBoundToBookingSummaryBound = (b: FinnairBoundItem, boundType: BoundType): BookingSummaryBound => ({
  ...b,
  type: boundType,
});

export const currentOfferSelection = (): StateSelector<BookingAppState, BookingSummaryBound[]> =>
  safeSelect(currentOfferSelectionSelector);

export const currentPassengers = (): StateSelector<BookingAppState, BookingSummaryPassenger[]> =>
  safeSelect(_currentPassengers);

export const currentServices = (): StateSelector<BookingAppState, BookingSummaryService[]> =>
  safeSelect(selectCurrentServices);

export const combinedCartServicesMap = (): StateSelector<BookingAppState, Record<string, FinnairServiceItem>> =>
  safeSelect(combinedCartServicesMapSelector);

export interface CartOfferPriceMismatch {
  cartId: string;
  // The passenger this comparison is for, or null for the whole-journey (all passengers combined) comparison.
  passengerId: string | null;
  cartAmount: string;
  offerAmount: string;
  offerOutboundPrice: string;
  offerOutboundId: string;
  offerInboundPrice: string;
  offerInboundId: string | undefined;
}

// A zero cart amount means pricing has not settled yet; there is nothing meaningful to compare in that case.
const _cartOfferPriceMismatch = (
  cartId: string,
  passengerId: string | null,
  cartAmount: PreciseNumber,
  offerAmount: PreciseNumber,
  outboundOfferPrice: string,
  outboundOfferId: string,
  inboundOfferPrice: string,
  inboundOfferId: string | undefined
): CartOfferPriceMismatch | null => {
  if (cartAmount.equals(PreciseNumber.ZERO) || cartAmount.equals(offerAmount)) {
    return null;
  }
  return {
    cartId,
    passengerId,
    cartAmount: cartAmount.toString(),
    offerAmount: offerAmount.toString(),
    offerOutboundPrice: outboundOfferPrice,
    offerOutboundId: outboundOfferId,
    offerInboundPrice: inboundOfferPrice,
    offerInboundId: inboundOfferId,
  };
};

// The price shown per bound comes from the offer; the amount the customer is charged comes from the cart. They
// should agree, so this reports the first disagreement found: the whole journey first, then each non-infant
// passenger (infants have no dedicated tab in the UI and were never validated on their own before this selector).
export const unexpectedMismatchBetweenCartAndOfferPricesSelector = createSelector(
  cartDataSelector,
  selectedOutboundFareFamilySelector,
  selectedInboundFareFamilySelector,
  (cart: FinnairCart, outboundOffer: FinnairBoundFareFamily, inboundOffer: FinnairBoundFareFamily) => {
    if (!cart || !outboundOffer) {
      return null;
    }

    const wholeJourneyMismatch = _cartOfferPriceMismatch(
      cart.id,
      null,
      PreciseNumber.fromString(cart.prices?.unpaid?.flight?.total?.totalAmount?.amount ?? '0'),
      PreciseNumber.fromString(outboundOffer.price).add(PreciseNumber.fromString(inboundOffer?.price ?? '0')),
      outboundOffer.price,
      outboundOffer.id,
      inboundOffer?.price ?? '0',
      inboundOffer?.id
    );
    if (wholeJourneyMismatch) {
      return wholeJourneyMismatch;
    }

    const nonInfantPassengers = cart.passengers?.filter((p) => p.passengerTypeCode !== FinnairPassengerCode.INF) ?? [];
    for (const passenger of nonInfantPassengers) {
      const outboundPaxPriceEntry = priceForPassengerTypeCode(outboundOffer, passenger.passengerTypeCode);
      const inboundPaxPriceEntry = inboundOffer
        ? priceForPassengerTypeCode(inboundOffer, passenger.passengerTypeCode)
        : undefined;
      // No per-passenger price breakdown on the offer for this passenger (a backend data gap): there is
      // nothing meaningful to compare, so don't default to 0 and risk a false mismatch against a real total.
      if (!outboundPaxPriceEntry || (inboundOffer && !inboundPaxPriceEntry)) {
        continue;
      }
      const outboundPaxPrice = outboundPaxPriceEntry.price ?? 0;
      const inboundPaxPrice = inboundPaxPriceEntry?.price ?? 0;
      const passengerMismatch = _cartOfferPriceMismatch(
        cart.id,
        passenger.id,
        PreciseNumber.fromString(cart.prices?.unpaid?.flight?.totalPerPax?.[passenger.id]?.totalAmount?.amount ?? '0'),
        PreciseNumber.fromString((outboundPaxPrice + inboundPaxPrice).toFixed(2)),
        String(outboundPaxPrice),
        outboundOffer.id,
        String(inboundPaxPrice),
        inboundOffer?.id
      );
      if (passengerMismatch) {
        return passengerMismatch;
      }
    }

    return null;
  }
);
